import React, { useMemo, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import Button from '@atlaskit/button';
import WarningIcon from '@atlaskit/icon/glyph/warning';
import { Box, Stack, xcss, Inline } from '@atlaskit/primitives';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { ff } from '@atlassian/jira-feature-flagging';
import { useIntl } from '@atlassian/jira-intl';

import { useTenantContext } from '@atlassian/jira-tenant-context-controller';
import Cards from '../../../common/assets/Cards.svg';
import FieldIllustration from '../../../common/assets/FieldIllustration.svg';
import ProjectIllustration from '../../../common/assets/ProjectIllustration.svg';
import ReviewImportUsers from '../../../common/assets/ReviewImportUsers.svg';
import { WHAT_IS_AN_ISSUE_LINK } from '../../../common/constants';
import { CSVReviewTilesType } from '../../../common/constants/steps';
import { Card } from '../../../common/ui/review-tile-card';
import { useProjectData } from '../../../controllers/import-state';
import { useCSVReviewData } from '../../../controllers/import-state/selectors';
import messages from './messages';
import { useContent } from './utils';

export const ReviewTiles = ({ tiles }: { tiles: CSVReviewTilesType[] }) => {
	const { formatMessage } = useIntl();
	const [projectData] = useProjectData();
	const { projectType, projectStyle, projectTemplate } = useContent(projectData);
	const [
		{
			numOfImportedFields,
			numOfTotalFields,
			numOfExistingFields,
			numOfCustomFields,
			numOfImportedUsers,
			numOfUniqueImportedUsers,
			numOfTotalUsers,
			numOfIssue,
		},
	] = useCSVReviewData();
	const { isAdmin, isSiteAdmin } = useTenantContext();
	const projectDetailsTile = useMemo(
		() => (
			<Box xcss={cardStyles} key={CSVReviewTilesType.PROJECT_DETAILS}>
				<Card
					icon={
						<Icon
							src={ProjectIllustration}
							aria-label={formatMessage(messages.projectDetailsTileLabel)}
						/>
					}
					heading={formatMessage(messages.projectDetailsTileHeading, {
						projectName: projectData.name,
					})}
					description={formatMessage(messages.projectDetailsTileDescription, {
						projectStyle,
						projectType,
						projectTemplate,
					})}
					testId="project-create-importer-upload-flow.ui.review-screen-standard.review-tiles.project-details"
					hasBoxShadow={false}
				/>
			</Box>
		),
		[formatMessage, projectData.name, projectStyle, projectTemplate, projectType],
	);

	const fieldsTile = useMemo(
		() => (
			<Box xcss={cardStyles} key={CSVReviewTilesType.FIELDS}>
				<Card
					icon={
						<Icon src={FieldIllustration} aria-label={formatMessage(messages.fieldTileLabel)} />
					}
					heading={formatMessage(messages.fieldTileHeading, {
						numOfImported: numOfImportedFields,
						numOfTotal: numOfTotalFields,
					})}
					description={
						<>
							{formatMessage(messages.fieldTileDescription, {
								numOfExistingFields,
							})}
							{numOfCustomFields > 0 &&
								formatMessage(messages.fieldTileDescriptionForCustomField, {
									numOfCustomFields,
								})}
						</>
					}
					testId="project-create-importer-upload-flow.ui.review-screen-standard.review-tiles.fields"
					hasBoxShadow={false}
				/>
			</Box>
		),
		[formatMessage, numOfCustomFields, numOfExistingFields, numOfImportedFields, numOfTotalFields],
	);

	const descriptionForUsersTile = useMemo(
		() => (
			<Stack space="space.050">
				<Inline>
					<span>
						{numOfImportedUsers > 0
							? `${formatMessage(messages.usersTileDescription, {
									numOfImported: numOfImportedUsers,
									numOfTotal: numOfTotalUsers,
								})} ${formatMessage(messages.usersTileMoreDescription, {
									numOfUniqueImported: numOfUniqueImportedUsers,
								})}`
							: formatMessage(messages.noUsersTileDescription)}
					</span>
				</Inline>
				{ff('enable-product-admin-for-x2c-migration_zmlsv') && !isSiteAdmin && isAdmin && (
					<Inline>
						<WarningIcon
							label=""
							size="medium"
							primaryColor={token('color.icon.warning', colors.Y400)}
						/>
						<span>{formatMessage(messages.usersTileWarningDescription)}</span>
					</Inline>
				)}
			</Stack>
		),
		[
			formatMessage,
			isAdmin,
			isSiteAdmin,
			numOfImportedUsers,
			numOfTotalUsers,
			numOfUniqueImportedUsers,
		],
	);

	const usersTile = useMemo(
		() => (
			<Box xcss={cardStyles} key={CSVReviewTilesType.USERS}>
				<Card
					icon={
						<Icon src={ReviewImportUsers} aria-label={formatMessage(messages.usersTileLabel)} />
					}
					heading={
						numOfImportedUsers > 0
							? formatMessage(messages.usersTileHeading, {
									numOfUniqueImported: numOfUniqueImportedUsers,
								})
							: formatMessage(messages.noUsersTileTitle)
					}
					description={descriptionForUsersTile}
					testId="project-create-importer-upload-flow.ui.review-screen-standard.review-tiles.users"
					hasBoxShadow={false}
				/>
			</Box>
		),
		[descriptionForUsersTile, formatMessage, numOfImportedUsers, numOfUniqueImportedUsers],
	);

	const issuesTile = useMemo(
		() => (
			<Box xcss={cardStyles} key={CSVReviewTilesType.ISSUES}>
				<Card
					icon={<Icon src={Cards} aria-label={formatMessage(messages.issuesTileLabel)} />}
					heading={formatMessage(messages.issuesTileHeading, {
						numOfIssue,
					})}
					description={formatMessage(
						numOfIssue === '0' ? messages.noIssuesTileDescription : messages.issuesTileDescription,
						{
							link: (content: ReactNode) => (
								<Button
									appearance="link"
									spacing="none"
									target="_blank"
									href={WHAT_IS_AN_ISSUE_LINK}
								>
									{content}
								</Button>
							),
						},
					)}
					testId="project-create-importer-upload-flow.ui.review-screen-standard.review-tiles.issues"
					hasBoxShadow={false}
				/>
			</Box>
		),
		[formatMessage, numOfIssue],
	);

	const tilesMap = useMemo(
		() => ({
			[CSVReviewTilesType.PROJECT_DETAILS]: projectDetailsTile,
			[CSVReviewTilesType.FIELDS]: fieldsTile,
			[CSVReviewTilesType.USERS]: usersTile,
			[CSVReviewTilesType.ISSUES]: issuesTile,
		}),
		[fieldsTile, issuesTile, projectDetailsTile, usersTile],
	);

	return <Box xcss={wrapperStyles}>{tiles.map((type) => tilesMap[type])}</Box>;
};

const wrapperStyles = xcss({
	width: '100%',
	paddingTop: 'space.300',
	paddingBottom: 'space.300',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Icon = styled.img({
	width: '60%',
});

const cardStyles = xcss({
	marginBlockStart: 'space.050',
});
